import type { ComponentProps } from 'react';
import React, { forwardRef, useRef } from 'react';
import type { ViewProps } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import { PanGestureHandler } from 'react-native-gesture-handler';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import useMergedRef from '@react-hook/merged-ref';

import { theme } from '@garnish/constants';
import { usePressableState } from '../../hooks';
import { webOnlyStyles } from '../../utils';

export const Card = forwardRef<View, CardProps>((props, ref) => {
  const {
    style: externalStyle,
    children,
    isSelected,
    disabled,
    onGestureEvent,
    baseBackgroundColor,
    ...restProps
  } = props;

  // ─── PRESSABLE STATE ─────────────────────────────────────────────

  const cardLocalRef = useRef(null);
  const cardRef = useMergedRef(cardLocalRef, ref);

  const { isHovered, isFocused } = usePressableState(cardLocalRef);
  const isHighlighted = isHovered || isFocused;
  const hasOnPress = Boolean(restProps.onPress && !disabled);

  // ─── STYLES ──────────────────────────────────────────────────────

  const cardAnimatedStyles = useCardAnimatedStyles({
    isHighlighted,
    isSelected,
    hasOnPress,
    disabled,
    baseBackgroundColor,
  });

  const cardWebOnlyStyles = webOnlyStyles({
    outlineColor: theme.colors.GRAY,
    outlineOffset: 3,
    cursor: hasOnPress ? 'pointer' : 'default',
  });
  const cardDisabledStyle = disabled && [
    webOnlyStyles({ cursor: 'not-allowed' }),
  ];
  const cardStyle = [
    styles.card,
    cardWebOnlyStyles,
    cardAnimatedStyles,
    cardDisabledStyle,
    externalStyle,
  ];

  // ─────────────────────────────────────────────────────────────────

  return (
    <CardContainer onGestureEvent={onGestureEvent}>
      <AnimatedPressable
        ref={cardRef}
        style={cardStyle}
        disabled={disabled}
        accessibilityRole="button"
        {...restProps}
      >
        {children}
      </AnimatedPressable>
    </CardContainer>
  );
});

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const CardContainer = (props: CardContainerProps) => {
  const { onGestureEvent, children } = props;

  if (!onGestureEvent)
    return <View style={styles.cardContainer}>{children}</View>;

  return (
    <PanGestureHandler
      onGestureEvent={onGestureEvent}
      activeOffsetX={[-10, 10]}
    >
      {children}
    </PanGestureHandler>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

const useCardAnimatedStyles = (props: UseCardStylesProps) => {
  const {
    isHighlighted,
    isSelected,
    disabled,
    hasOnPress,
    baseBackgroundColor = theme.colors.QUINOA,
  } = props;

  const animatedBackgroundColor = useDerivedValue(() => {
    const idleValues = isHighlighted ? 1 : 2;
    const selectedValues = isHighlighted ? 4 : 3;

    const pressableValue = isSelected ? selectedValues : idleValues;
    const defaultValue = disabled ? 0 : 2;
    const animatedValue = hasOnPress ? pressableValue : defaultValue;

    return withTiming(animatedValue, { duration: theme.transitions.base });
  }, [isHighlighted, hasOnPress, isSelected, disabled]);

  const animatedBorderColor = useDerivedValue(() => {
    return withTiming(isSelected ? 1 : 0, { duration: theme.transitions.base });
  }, [isSelected]);

  return useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        animatedBackgroundColor.value,
        [0, 1, 2, 3, 4],
        [
          theme.colors.LINEN,
          theme.colors.QUINOA_HOVER,
          baseBackgroundColor,
          theme.colors.CUCUMBER,
          theme.colors.CUCUMBER_HOVER,
        ],
      ),

      borderColor: interpolateColor(
        animatedBorderColor.value,
        [0, 1],
        [theme.colors.OPACITY.TRANSPARENT, theme.colors.SPINACH],
      ),
    };
  }, [baseBackgroundColor]);
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-start',
    backgroundColor: theme.colors.QUINOA,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.colors.OPACITY.TRANSPARENT,
    borderRadius: theme.radius.medium,
  },
  cardContainer: {
    flexGrow: 1,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CardProps = {
  readonly isSelected?: boolean;
  readonly disabled?: boolean;
  readonly onGestureEvent?: OnGestureEvent;
  readonly style?: ViewProps['style'];
  readonly baseBackgroundColor?: string;
} & Omit<ComponentProps<typeof Pressable>, 'style'>;

type UseCardStylesProps = {
  readonly isHighlighted: boolean;
  readonly hasOnPress: boolean;
  readonly disabled?: boolean;
  readonly isSelected?: boolean;
  readonly baseBackgroundColor?: string;
};

type CardContainerProps = {
  readonly onGestureEvent?: OnGestureEvent;
} & Pick<ViewProps, 'children'>;

type OnGestureEvent = ComponentProps<
  typeof PanGestureHandler
>['onGestureEvent'];
